import moment from "moment";
import ListFilter from "../../types/ListFilter";
import PatientListEntry from "../../types/PatientListEntry";

export const applyGridFilter = (
  data: PatientListEntry[],
  filter: ListFilter,
  columnFieldMap: Record<string, any>
): PatientListEntry[] => {
  return data.filter((p) => {
    const rawValue = p[filter.field];
    const colDef =
      columnFieldMap[filter.source_field ? filter.source_field : filter.field];

    if (!colDef) return false;

    // if the target value is blank, we're clearing the filter
    if (
      filter.value === "" ||
      (Array.isArray(filter.value) && filter.value.length === 0)
    )
      return true;

    const getPatientValue = () => {
      if (Object.hasOwn(colDef, "valueGetter")) {
        const patientValue = colDef.valueGetter(rawValue, p);
        // for aggregated fields, we'll return an object
        if (
          patientValue &&
          typeof patientValue === "object" &&
          !Array.isArray(patientValue)
        ) {
          return patientValue[filter.field];
        }

        return patientValue;
      }
      return rawValue;
    };

    // get the valueGetter for this column, if any
    const patientValue = getPatientValue();

    // null patient values are always false unless we're clearing filters
    if (patientValue === null) {
      return false;
    }

    // for numbers, we use a range
    if (filter.filter_type === "number") {
      // if both sides of the range are null, return all
      if (filter.value.length === 0) {
        return true;
      }

      // if one of the sides is null, then we use it as an unbounded range
      if (filter.value[0] === null) {
        return patientValue <= filter.value[1];
      } else if (filter.value[1] === null) {
        return filter.value[0] <= patientValue;
      }

      return filter.value[0] <= patientValue && patientValue <= filter.value[1];
    } else if (filter.filter_type === "multi") {
      // if the filter uses multiple values, it will have an array for the results and row value
      // unless we have a special case like dispo where we join the patient value for the row
      const finalValue = Array.isArray(patientValue)
        ? patientValue
        : patientValue.split("|");
      const hasValue =
        Array.isArray(filter.value) &&
        filter.value.filter((fv) => {
          // however, the row value is a string if there is no getter
          if (!Array.isArray(finalValue)) {
            return finalValue.toUpperCase().includes(fv.toUpperCase());
          }
          return finalValue
            .map((pv) => pv.toUpperCase())
            .includes(fv.toUpperCase());
        });
      return hasValue.length > 0;
    } else if (filter.filter_type === "multi_date") {
      const dateValue = filter.filter_format
        ? moment(patientValue, filter.filter_format).toDate().toDateString()
        : moment(patientValue).toDate().toDateString();

      // handle multiple choice date range options
      const matchesRange =
        Array.isArray(filter.value) &&
        filter.value.filter((fv) => {
          const currentDate = moment();
          const lower = fv.toLowerCase();

          if (lower.includes("today")) {
            return dateValue === currentDate.toDate().toDateString();
          } else if (lower.includes("tomorrow")) {
            return (
              dateValue === currentDate.add(1, "d").toDate().toDateString()
            );
          } else if (lower.includes("days")) {
            // get all numbers from the string
            const nums = lower.match(/\d+(\.\d+)?/g);
            // there should only be one
            const numDays = nums && nums.length > 0 ? Number(nums[0]) : 0;

            return (
              numDays > 0 &&
              dateValue ===
                currentDate.add(numDays, "d").toDate().toDateString()
            );
          }
          return false;
        });

      return matchesRange.length > 0;
    } else {
      return (
        String(patientValue).toUpperCase() ===
        String(filter.value).toUpperCase()
      );
    }
  });
};
